import type { PortalAccountSummary } from '~/types/portal'

/**
 * Client-account session for one tenant's portal. The cookie NAME carries
 * the tenant slug (`portal-token-{slug}`) rather than relying on cookie
 * scoping, since — unlike the agent app's subdomain-per-tenant hosts —
 * every tenant's client portal lives under the SAME frontend origin at
 * /client/{tenantSlug}/..., so a slug-less cookie would leak one tenant's
 * session into another tenant's portal pages in the same browser.
 */
export const usePortalAuth = (tenantSlug: string) => {
  const token = useCookie<string | null>(`portal-token-${tenantSlug}`, {
    sameSite: 'lax',
    secure: import.meta.env.PROD,
    maxAge: 60 * 60 * 24 * 30
  })

  const account = useState<PortalAccountSummary | null>(`portal-account-${tenantSlug}`, () => null)

  const isAuthenticated = computed(() => !!token.value)

  const setSession = (newToken: string, newAccount: PortalAccountSummary) => {
    token.value = newToken
    account.value = newAccount
  }

  const clearSession = () => {
    token.value = null
    account.value = null
  }

  return { token, account, isAuthenticated, setSession, clearSession }
}
